// One name input, two "Create as X" choice buttons.
//
// The dialog has a single shared text field; the user types a name then
// picks whether to create it as a project (sub-project of the current
// project) or as a task in the current project. The controller POSTs the
// right form-encoded payload to the right endpoint; on success we reload
// the page so the new item appears in the tree.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller<HTMLElement> {
  static targets = ["name", "projectBtn", "taskBtn"]
  declare readonly nameTarget: HTMLInputElement
  declare readonly projectBtnTarget: HTMLButtonElement
  declare readonly taskBtnTarget: HTMLButtonElement

  static values = { projectUrl: String, taskUrl: String, customerId: String, parentId: String }
  declare readonly projectUrlValue: string
  declare readonly taskUrlValue: string
  declare readonly customerIdValue: string
  declare readonly parentIdValue: string

  connect(): void {
    this.updateValidity()
  }

  nameChanged(): void {
    this.updateValidity()
  }

  createProject(event: Event): void {
    event.preventDefault()
    if (!this.canSubmit()) return
    const fd = new FormData()
    fd.append("project[name]", this.nameTarget.value)
    fd.append("customer_id", this.customerIdValue)
    fd.append("parent_id",   this.parentIdValue)
    this.submit(this.projectUrlValue, fd)
  }

  createTask(event: Event): void {
    event.preventDefault()
    if (!this.canSubmit()) return
    const fd = new FormData()
    fd.append("task[name]",  this.nameTarget.value)
    fd.append("project_id",  this.parentIdValue)
    this.submit(this.taskUrlValue, fd)
  }

  private canSubmit(): boolean {
    return this.nameTarget.value.trim().length > 0
  }

  private updateValidity(): void {
    // Just flip the native `disabled` attribute — the shared button
    // component (app/views/components/_button.html.haml) owns the visual
    // disabled state via `disabled:*` Tailwind variants, so both buttons
    // here read the same as the single-kind create modals' Create button.
    const disabled = !this.canSubmit()
    this.projectBtnTarget.disabled = disabled
    this.taskBtnTarget.disabled = disabled
  }

  private async submit(url: string, fd: FormData): Promise<void> {
    const csrf =
      (document.querySelector("meta[name=csrf-token]") as HTMLMetaElement | null)?.content ?? ""
    fd.append("authenticity_token", csrf)
    try {
      const res = await fetch(url, {
        method: "POST",
        body: fd,
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/html" },
      })
      if (res.ok || res.redirected) {
        window.location.reload()
      } else {
        window.alert("Could not create. Please try a different name.")
      }
    } catch {
      window.alert("Could not create — check your connection and try again.")
    }
  }
}
